import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import toast from "react-hot-toast";
import { UserAPI } from "@repo/api/user";
import { Button, Checkbox, Input, Label, Link, Spinner, TextField } from "@heroui/react";
import BridgeHelper from "@/utils/rn-bridge/bridge-helper";
import { NavCode } from "@/utils/rn-bridge/nav-code";
import { faceVerifyFailSearch, getErrorMessage, parseFaceIdVerifyResult } from "./-utils/face-id";

export const Route = createFileRoute("/help/account_recovery/id")({
    component: RecoveryByRealID,
});

export default function RecoveryByRealID() {
    const navigate = useNavigate();
    const router = useRouter();
    const [realName, setRealName] = useState<string>("");
    const [idNumber, setIdNumber] = useState<string>("");
    const [submitLoading, setSubmitLoading] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);

    const handleSubmit = async () => {
        if (realName.length === 0 || idNumber.length === 0) {
            toast.error("请输入有效的真实姓名和身份证号");
            return;
        }
        if (!consentChecked) {
            toast.error("请阅读并同意面部信息收集说明");
            return;
        }

        setSubmitLoading(true);
        // 人脸识别一旦拉起，后续任何失败都进入失败结果页，要求用户重新开始
        let faceStarted = false;
        const goFail = (message: string) =>
            navigate({ to: "/result/fail", search: faceVerifyFailSearch(message), replace: true });
        try {
            const { data: ticketData } = await UserAPI.findUserByRealName(realName, idNumber);
            if (!ticketData.ticket) return;

            const { data: faceIdData } = await UserAPI.getRecoveryFaceIdToken(ticketData.ticket);
            if (!faceIdData) return;

            faceStarted = true;
            const rawResult = await BridgeHelper.sendMessageResult(
                {
                    nav_code: NavCode.NAV_FACEID_VERIFY,
                    data: faceIdData,
                },
                120_000,
            );

            const { code, result } = parseFaceIdVerifyResult(rawResult);
            if (code !== 1) {
                goFail(result || "认证失败");
                return;
            }

            const { data: verifyData } = await UserAPI.verifyRecoveryFaceId(
                ticketData.ticket,
                faceIdData.orderNo,
                faceIdData.nonce,
            );

            navigate({
                to: "/user/account/recovery",
                search: {
                    recovery_ticket: ticketData.ticket,
                    session_code: verifyData.session_code,
                },
                replace: true,
            });
        } catch (err) {
            if (faceStarted) {
                goFail(getErrorMessage(err));
                return;
            }
            toast.error(getErrorMessage(err));
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div className={"flex flex-col bg-surface h-screen max-w-md mx-auto px-6"}>
            <section className="mt-12 mb-6">
                <div className="text-2xl text-black-800 text-center font-bold mb-2">通过实名信息找回</div>
                <div className="text-md text-center text-black-800">非大陆证件请通过帮助中心添加客服QQ群找回</div>
            </section>
            <div className="flex flex-col gap-4">
                <TextField
                    name="realName"
                    fullWidth
                    isDisabled={submitLoading}
                    value={realName}
                    onChange={setRealName}
                >
                    <Label>真实姓名</Label>
                    <Input placeholder="请输入实名时使用的真实姓名" />
                </TextField>
                <TextField
                    name="idNumber"
                    fullWidth
                    isDisabled={submitLoading}
                    value={idNumber}
                    onChange={setIdNumber}
                >
                    <Label>证件号码</Label>
                    <Input placeholder="请输入实名时使用的证件号码" />
                </TextField>
            </div>
            <Checkbox
                name="face-id-consent"
                className="my-4"
                isSelected={consentChecked}
                isDisabled={submitLoading}
                onChange={setConsentChecked}
            >
                <Checkbox.Content>
                    <Checkbox.Control className="mt-0.5">
                        <Checkbox.Indicator />
                    </Checkbox.Control>
                    <span className="text-sm text-gray-600 leading-relaxed">
                        我们将会进一步收集您的面部信息特征用于账号真实性核验。如果您拒绝授权，您将无法完成认证，勾选并提交代表您单独同意。
                    </span>
                </Checkbox.Content>
            </Checkbox>
            <section className={"flex flex-col items-center p-4 mt-1 gap-3"}>
                <Button
                    className="w-full rounded-full"
                    onPress={handleSubmit}
                    isPending={submitLoading}
                    isDisabled={submitLoading || idNumber.length < 18 || realName === "" || !consentChecked}
                >
                    {({ isPending }) => (
                        <>
                            {isPending && <Spinner size="sm" color="current" />}
                            确认
                        </>
                    )}
                </Button>
                <Link className="text-sm text-primary no-underline" onPress={() => router.history.back()}>
                    我无法通过这个方式找回
                </Link>
            </section>
        </div>
    );
}
